// Migrado de `xlsx` (SheetJS, sin soporte de estilos) a `exceljs` (Task 11) — `xlsx` fue removido
// de package.json. `agregarHojaDesdeObjetos` replica el comportamiento real de
// `XLSX.utils.json_to_sheet`: la cabecera es la UNIÓN de las claves de TODAS las filas, en orden de
// primera aparición (no solo las claves del primer objeto) — una fila con menos claves que otra no
// trunca las columnas del resto del libro. Cada fila se exporta con una celda vacía (`undefined`)
// en cualquier columna cuya clave no traiga. La descarga (buffer → Blob → <a download>) vive en
// `excelDownload.ts`, compartida con `exportExcelMultiSheet.ts` (mismo mecanismo, extraído para no
// duplicarlo).
import ExcelJS from 'exceljs';
import type { SurveyExportData } from '@/api/types';
import { descargarWorkbookComoExcel } from './excelDownload';

const EXTENSION_EXCEL = '.xlsx';

/** Une las claves de todas las filas, en orden de primera aparición (criterio de
 *  `XLSX.utils.json_to_sheet`): una fila con un juego de claves distinto a las demás no trunca ni
 *  reordena las columnas del resto. */
function columnasUnionDeFilas(rows: ReadonlyArray<Record<string, unknown>>): string[] {
  const vistas = new Set<string>();
  rows.forEach((row) => {
    Object.keys(row).forEach((clave) => vistas.add(clave));
  });
  return Array.from(vistas);
}

export function agregarHojaDesdeObjetos(
  workbook: ExcelJS.Workbook, sheetName: string, rows: ReadonlyArray<Record<string, unknown>>,
): void {
  const worksheet = workbook.addWorksheet(sheetName);
  const columnas = columnasUnionDeFilas(rows);
  worksheet.addRow(columnas);
  rows.forEach((row) => worksheet.addRow(columnas.map((columna) => row[columna])));
}

export async function exportToExcel<T extends Record<string, unknown>>(
  data: T[], filename: string, sheetName = 'Sheet1',
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  agregarHojaDesdeObjetos(workbook, sheetName, data);
  await descargarWorkbookComoExcel(workbook, `${filename}${EXTENSION_EXCEL}`);
}

export async function exportSurveyToExcel(data: SurveyExportData, filename: string): Promise<void> {
  const workbook = new ExcelJS.Workbook();

  // Sheet 1: General info
  const generalRows = [
    { Field: 'Name', Value: data.generalInfo.name },
    { Field: 'Description', Value: data.generalInfo.description ?? '' },
    { Field: 'Type', Value: data.generalInfo.type },
    { Field: 'Status', Value: data.generalInfo.status },
    { Field: 'Audience OUs', Value: data.generalInfo.ouIds.join(', ') },
    { Field: 'Response Count', Value: data.generalInfo.responseCount },
    { Field: 'Response Rate (%)', Value: data.generalInfo.responseRate },
    { Field: 'Expires At', Value: data.generalInfo.expiresAt ?? '' },
    { Field: 'Is Approved', Value: data.generalInfo.isApproved ? 'Yes' : 'No' },
    { Field: 'Created By', Value: data.generalInfo.createdBy },
    { Field: 'Created At', Value: data.generalInfo.createdAt },
  ];
  agregarHojaDesdeObjetos(workbook, 'General', generalRows);

  // Sheet 2: Questions
  const questionRows = data.questions.map(q => ({
    '#': q.order,
    Text: q.text,
    Type: q.type,
    Fundamental: q.fundamentalId ?? '',
    'Daily Habit': q.dailyHabitId ?? '',
  }));
  agregarHojaDesdeObjetos(workbook, 'Questions', questionRows);

  // Sheet 3: Feedbacks
  const feedbackRows = data.feedbacks.map(f => ({
    'Evaluator ID': f.evaluatorId,
    'Evaluated Name': f.evaluatedDisplayName ?? '',
    'Response Date': f.respondedAt,
    Question: f.questionText,
    Answer: f.answer,
  }));
  agregarHojaDesdeObjetos(workbook, 'Feedbacks', feedbackRows);

  await descargarWorkbookComoExcel(workbook, `${filename}${EXTENSION_EXCEL}`);
}
